import { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../auth/authContext.ts";
import { HipotesisInline } from "../../components/HipotesisNormativas.tsx";
import {
  actualizarArrendamiento,
  apiConfigurada,
  type ArrendamientoRow,
  type AportacionRow,
  cobrosDeArrendamiento,
  crearAportacion,
  crearArrendamiento,
  evaluacionesUltimas,
  hipotesisPorAportaciones,
  listarArrendamientos,
  listarCatalogo,
  listarPersonas,
  listarProyectos,
  type CatalogoRow,
  type EvaluacionMotor,
  type HipotesisNormativa,
  type PersonaRow,
  type ProyectoMin,
} from "../../lib/api.ts";
import { C, st, dinero, fecha } from "../../ui/theme.ts";
import { Chip, Fila } from "../../ui/componentes.tsx";
import { IconoLlave } from "../../ui/iconos.tsx";
import { nombrePersona } from "../../ui/persona.ts";
import { faltantesDatosAri } from "./validacionesRec.ts";

/**
 * ARRENDAMIENTOS — captura de contratos de arrendamiento (fr. XV, OT-7) espejo
 * REDUCIDO de CompraventasView (fr. V): contenedor comercial (inmueble,
 * arrendatario, renta pactada, vigencia) sin etapa/preventa/instrumento público
 * (el XSD ARI no los define). Los COBROS DE RENTA son aportaciones evaluadas por
 * el motor real, con acumulación MENSUAL propia de fr. XV. El formato oficial
 * SAT ARI existe; Argus genera el XML y conserva el ciclo probatorio separado
 * de DIN/INM.
 */

const OPACIDAD_DESHABILITADO = 0.6;
const LONGITUD_FOLIO_REAL = 200;
const LONGITUD_DIRECCION_ARI = 200;
const LONGITUD_NUMERO_DIRECCION_ARI = 40;

const INSTRUMENTOS: [string, string][] = [
  ["1", "Efectivo"],
  ["2", "Tarjeta de Crédito"],
  ["3", "Tarjeta de Debito"],
  ["4", "Tarjeta de Prepago"],
  ["5", "Cheque Nominativo"],
  ["6", "Cheque de Caja"],
  ["7", "Cheques de Viajero"],
  ["8", "Transferencia Interbancaria"],
  ["9", "Transferencia Misma Institución"],
  ["10", "Transferencia Internacional"],
  ["11", "Orden de Pago"],
  ["12", "Giro"],
  ["13", "Oro o Platino Amonedados"],
  ["14", "Plata Amonedada"],
  ["15", "Metales Preciosos"],
  ["16", "Activos Virtuales"],
  ["99", "Otros"],
];

const FORMAS_PAGO_ARI: [string, string][] = [
  ["1", "Contado"],
  ["2", "Diferido o en parcialidades"],
  ["3", "Dación en pago"],
  ["4", "Préstamo o crédito"],
  ["5", "Permuta"],
];

const TIPOS_INMUEBLE: [string, string][] = [
  ["1", "Casa /Casa en condominio"],
  ["2", "Departamento"],
  ["3", "Edificio habitacional"],
  ["4", "Edificio comercial"],
  ["5", "Edificio oficinas"],
  ["6", "Local comercial independiente"],
  ["7", "Local en centro comercial"],
  ["8", "Oficina"],
  ["9", "Bodega comercial"],
  ["10", "Bodega industrial"],
  ["11", "Nave Industrial"],
  ["12", "Terreno urbano habitacional"],
  ["13", "Terreno no urbano habitacional"],
  ["14", "Terreno urbano comercial o industrial"],
  ["15", "Terreno no urbano comercial o industrial"],
  ["16", "Terreno ejidal"],
  ["17", "Rancho/Hacienda/Quinta"],
  ["18", "Huerta"],
  ["99", "Otro"],
];

const MONEDAS_ARI: [string, string][] = [
  ["1", "Peso mexicano"],
  ["2", "Dólar estadounidense"],
  ["3", "Euro"],
];

type CatalogoOption = [string, string];

interface CatalogosAri {
  instrumentos: CatalogoOption[];
  monedas: CatalogoOption[];
  tiposInmueble: CatalogoOption[];
  formasPago: CatalogoOption[];
}

const CATALOGOS_ARI_FALLBACK: CatalogosAri = {
  instrumentos: INSTRUMENTOS,
  monedas: MONEDAS_ARI,
  tiposInmueble: TIPOS_INMUEBLE,
  formasPago: FORMAS_PAGO_ARI,
};

function opcionesCatalogo(rows: CatalogoRow[]): CatalogoOption[] {
  return rows.map((r) => [r.clave, r.nombre]);
}

function textoONull(v: string): string | null {
  const limpio = v.trim();
  return limpio === "" ? null : limpio;
}

function claveXml(v: string, max: number): string {
  return v.toUpperCase().slice(0, max);
}

function etiquetaCatalogo(cat: [string, string][], clave: string | null | undefined, fallback: string): string {
  if (!clave) return fallback;
  return cat.find(([k]) => k === clave)?.[1] ?? `${fallback} ${clave}`;
}

/** Veredicto del motor para un cobro de renta fr. XV, en español. */
function veredictoCobro(e: EvaluacionMotor | undefined): { tono: "verde" | "ambar" | "rojo" | "gris"; texto: string } | null {
  if (!e) return null;
  if (e.prohibicion_efectivo) {
    return { tono: "rojo", texto: "Efectivo sobre el límite legal (Art. 32) — el cobro no puede aplicarse; queda constancia." };
  }
  if (e.prohibicion_indeterminada) {
    return { tono: "gris", texto: "Límite de efectivo indeterminado a la fecha — revisión del REC." };
  }
  if (e.requiere_aviso) {
    if (e.formato_aviso === "ARRENDAMIENTO_XML") {
      return { tono: "ambar", texto: "Cruza el umbral MENSUAL: genera exportación XML de Arrendamiento y conserva ciclo de validación, presentación y acuse separado del DIN/INM." };
    }
    return { tono: "ambar", texto: "Cruza el umbral: genera aviso ligado al informe del mes." };
  }
  return { tono: "verde", texto: "Sin obligación de aviso; suma en la ventana MENSUAL de fr. XV (acumulación mensual, no de 6 meses)." };
}

export function ArrendamientosView() {
  const { session } = useAuth();
  const token = session?.accessToken ?? "";
  const tenantId = session?.tenantId ?? "";
  const demo = !apiConfigurada() || token === "";

  const [ars, setArs] = useState<ArrendamientoRow[]>([]);
  const [personas, setPersonas] = useState<PersonaRow[]>([]);
  const [proyectos, setProyectos] = useState<ProyectoMin[]>([]);
  const [selId, setSelId] = useState<string | null>(null);
  const [modo, setModo] = useState<"detalle" | "nueva">("detalle");
  const [cobros, setCobros] = useState<AportacionRow[] | null>(null);
  const [evals, setEvals] = useState<Map<string, EvaluacionMotor>>(new Map());
  const [hipotesis, setHipotesis] = useState<Map<string, HipotesisNormativa[]>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(!demo);
  const [ocupado, setOcupado] = useState(false);
  const [catalogos, setCatalogos] = useState<CatalogosAri>(CATALOGOS_ARI_FALLBACK);

  const sel = ars.find((a) => a.id === selId) ?? null;

  const recargar = useCallback(async () => {
    if (demo) return;
    const [xs, per, pro] = await Promise.all([listarArrendamientos(token), listarPersonas(token), listarProyectos(token)]);
    setArs(xs);
    setPersonas(per);
    setProyectos(pro);
  }, [demo, token]);

  const cargarCobros = useCallback(async (arId: string) => {
    if (demo) {
      setCobros([]);
      return;
    }
    setCobros(null);
    const filas = await cobrosDeArrendamiento(token, arId);
    setCobros(filas);
    const ids = filas.map((f) => f.id);
    const [ev, hn] = await Promise.all([evaluacionesUltimas(token, ids), hipotesisPorAportaciones(token, ids)]);
    setEvals(ev);
    setHipotesis(hn);
  }, [demo, token]);

  useEffect(() => {
    if (demo) return;
    setCargando(true);
    recargar()
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : "No se pudieron cargar los arrendamientos."); })
      .finally(() => { setCargando(false); });
  }, [demo, recargar]);

  useEffect(() => {
    if (demo) {
      setCatalogos(CATALOGOS_ARI_FALLBACK);
      return;
    }
    Promise.all([
      listarCatalogo(token, "cat_instrumento_monetario"),
      listarCatalogo(token, "cat_moneda"),
      listarCatalogo(token, "cat_inm_tipo_inmueble"),
      listarCatalogo(token, "cat_inm_forma_pago"),
    ])
      .then(([instrumentos, monedas, tiposInmueble, formasPago]) => {
        setCatalogos({
          instrumentos: opcionesCatalogo(instrumentos),
          monedas: opcionesCatalogo(monedas),
          tiposInmueble: opcionesCatalogo(tiposInmueble),
          formasPago: opcionesCatalogo(formasPago),
        });
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "No se pudieron cargar los catálogos oficiales ARI.");
      });
  }, [demo, token]);

  useEffect(() => {
    if (sel && modo === "detalle") {
      cargarCobros(sel.id).catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "No se pudieron cargar los cobros de renta.");
      });
    }
  }, [sel, modo, cargarCobros]);

  const total = (cobros ?? []).reduce((s, c) => s + (Number(c.monto_bruto) || 0), 0);

  return (
    <div className="argus-master-detail" style={{ display: "flex", flex: 1, minWidth: 0 }}>
      {/* Lista central */}
      <section className="argus-list-pane" style={{ width: 330, flexShrink: 0, borderRight: `1px solid ${C.line}`, display: "flex", flexDirection: "column", background: "#FBFCFB" }}>
        <div style={{ padding: "16px 16px 10px" }}>
          <h2 style={{ ...st.display, margin: 0, fontSize: 18 }}>Arrendamientos</h2>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: C.muted }}>
            Uso o goce de inmuebles (fr. XV). Los cobros de renta los evalúa el motor, acumulación mensual.
          </p>
        </div>
        {demo && (
          <div style={{ margin: "8px 16px", padding: "6px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 11.5, fontWeight: 600 }}>
            Modo demostración — conecta el backend para operar con el motor real.
          </div>
        )}
        <div style={{ flex: 1, overflow: "auto", padding: "6px 12px 8px" }}>
          {cargando && <p style={{ color: C.muted, fontSize: 13, padding: "0 6px" }}>Cargando…</p>}
          {!cargando && ars.length === 0 && (
            <p style={{ color: C.muted, fontSize: 13, padding: "0 6px" }}>Sin arrendamientos — registra el primero.</p>
          )}
          {ars.map((ar) => {
            const activa = selId === ar.id && modo === "detalle";
            return (
              <button
                key={ar.id}
                onClick={() => { setSelId(ar.id); setModo("detalle"); setError(null); }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "#fff",
                  border: `1px solid ${activa ? C.tiel : C.line}`,
                  boxShadow: activa ? `0 0 0 1px ${C.tiel}` : "none",
                  borderRadius: 12,
                  padding: "10px 12px",
                  marginBottom: 8,
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13.5, color: C.ink }}>{ar.unidad}</span>
                  <span style={{ fontWeight: 700, fontSize: 13, color: C.ink, whiteSpace: "nowrap" }}>{dinero(ar.renta_mensual)}/mes</span>
                </div>
                <div style={{ fontSize: 12, color: C.muted, margin: "2px 0 6px" }}>
                  {nombrePersona(ar.cliente_aportante)} · {ar.proyecto?.nombre ?? "—"}
                </div>
                <Chip tono="tiel">{fecha(ar.fecha_inicio)} → {fecha(ar.fecha_termino)}</Chip>
              </button>
            );
          })}
        </div>
        <div style={{ padding: 12, borderTop: `1px solid ${C.line}` }}>
          <button style={{ ...st.btnPrim, width: "100%" }} onClick={() => { setModo("nueva"); setSelId(null); setError(null); }}>
            ＋ Registrar arrendamiento
          </button>
        </div>
      </section>

      {/* Detalle / captura */}
      <main className="argus-detail-pane" style={{ flex: 1, overflow: "auto", padding: "22px 26px", minWidth: 0 }}>
        {error && (
          <div style={{ maxWidth: 560, margin: "0 auto 12px", padding: "10px 14px", borderRadius: 10, background: C.redBg, color: C.red, fontSize: 13 }}>
            {error}
          </div>
        )}
        {modo === "nueva" ? (
          <FormArrendamiento
            demo={demo}
            catalogos={catalogos}
            personas={personas}
            proyectos={proyectos}
            onCancelar={() => { setModo("detalle"); }}
            onCrear={async (datos) => {
              setError(null);
              try {
                if (demo) {
                  setError("En modo demostración el alta requiere backend conectado.");
                  return;
                }
                const fila = await crearArrendamiento(token, tenantId, datos);
                await recargar();
                setSelId(fila.id);
                setModo("detalle");
              } catch (e) {
                setError(e instanceof Error ? e.message : "No se pudo registrar el arrendamiento.");
              }
            }}
          />
        ) : sel ? (
          <div style={{ maxWidth: 560, margin: "0 auto" }}>
            <h2 style={{ margin: "0 0 2px", fontSize: 20 }}>{sel.unidad}</h2>
            <p style={{ margin: "0 0 10px", color: C.muted, fontSize: 13 }}>
              {nombrePersona(sel.cliente_aportante)} · {sel.proyecto?.nombre ?? "—"} · {fecha(sel.fecha_operacion)}
            </p>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
              <Chip tono="tiel">fr. XV · arrendamiento de inmuebles</Chip>
              <Chip tono="gris">{fecha(sel.fecha_inicio)} → {fecha(sel.fecha_termino)}</Chip>
              {sel.cliente_aportante && !sel.cliente_aportante.expediente_completo && (
                <Chip tono="ambar">Expediente del arrendatario incompleto</Chip>
              )}
            </div>

            <div style={st.tarjeta}>
              <p style={st.eyebrow}>Operación</p>
              <Fila k="Renta mensual pactada" v={dinero(sel.renta_mensual)} />
              <Fila k="Cobrado este período" v={dinero(total)} />
            </div>

            <DatosAriCard
              arrendamiento={sel}
              catalogos={catalogos}
              ocupado={ocupado}
              onGuardar={async (patch) => {
                setError(null);
                setOcupado(true);
                try {
                  if (demo) {
                    setError("En modo demostración la edición requiere backend conectado.");
                    return;
                  }
                  await actualizarArrendamiento(token, sel.id, patch);
                  await recargar();
                } catch (e) {
                  setError(e instanceof Error ? e.message : "No se pudieron guardar los datos ARI.");
                } finally {
                  setOcupado(false);
                }
              }}
            />

            <CobrosCard
              demo={demo}
              catalogos={catalogos}
              cobros={cobros}
              evals={evals}
              hipotesis={hipotesis}
              onRegistrar={async (datos) => {
                setError(null);
                setOcupado(true);
                try {
                  if (demo) {
                    throw new Error("En modo demostración el cobro requiere backend conectado.");
                  }
                  await crearAportacion(token, tenantId, {
                    ...datos,
                    arrendamiento_id: sel.id,
                    proyecto_id: sel.proyecto?.id,
                    cliente_aportante_id: sel.cliente_aportante?.id,
                  });
                  await cargarCobros(sel.id);
                } catch (e) {
                  setError(e instanceof Error ? e.message : "No se pudo registrar el cobro de renta.");
                  throw e;
                } finally {
                  setOcupado(false);
                }
              }}
            />

            <div style={{ ...st.tarjeta, background: C.tielSoft, borderColor: "#C8E0DD" }}>
              <p style={{ ...st.eyebrow, color: C.tielDark }}>Cómo funciona</p>
              <div style={{ fontSize: 12.5, color: C.ink, lineHeight: 1.7 }}>
                Cada cobro de renta se evalúa con doble base y ACUMULACIÓN MENSUAL propia de
                fr. XV (no la ventana de 6 meses de fr. V). El aviso queda registrado con su
                reloj; Argus genera el XML oficial de Arrendamiento (ARI) y conserva el ciclo
                de validación, presentación y acuse separado del DIN/INM.
              </div>
            </div>
          </div>
        ) : (
          <div style={{ maxWidth: 420, margin: "18vh auto 0", textAlign: "center", color: C.muted }}>
            <div style={{ marginBottom: 8, color: C.tielDark }}><IconoLlave size={34} /></div>
            <h3 style={{ margin: "0 0 6px", color: C.ink }}>Selecciona un arrendamiento</h3>
            <p style={{ fontSize: 13.5, margin: 0 }}>
              El inmueble, su arrendatario y sus cobros de renta — cada cobro evaluado por el
              motor, con acumulación mensual.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Datos ARI del inmueble/vigencia
// ─────────────────────────────────────────────────────────────────────────────

function DatosAriCard({
  arrendamiento,
  catalogos,
  ocupado,
  onGuardar,
}: {
  arrendamiento: ArrendamientoRow;
  catalogos: CatalogosAri;
  ocupado: boolean;
  onGuardar: (patch: Record<string, unknown>) => Promise<void>;
}) {
  const [tipoInmueble, setTipoInmueble] = useState(arrendamiento.tipo_inmueble ?? "1");
  const [colonia, setColonia] = useState(arrendamiento.inmueble_colonia ?? "");
  const [calle, setCalle] = useState(arrendamiento.inmueble_calle ?? "");
  const [numeroExterior, setNumeroExterior] = useState(arrendamiento.inmueble_numero_exterior ?? "");
  const [numeroInterior, setNumeroInterior] = useState(arrendamiento.inmueble_numero_interior ?? "");
  const [codigoPostal, setCodigoPostal] = useState(arrendamiento.inmueble_codigo_postal ?? "");
  const [folioReal, setFolioReal] = useState(arrendamiento.folio_real ?? "");
  const [fechaInicio, setFechaInicio] = useState(arrendamiento.fecha_inicio);
  const [fechaTermino, setFechaTermino] = useState(arrendamiento.fecha_termino);
  const [guardando, setGuardando] = useState(false);
  const faltantes = faltantesDatosAri({ colonia, calle, numeroExterior, codigoPostal, folioReal, fechaInicio, fechaTermino });

  useEffect(() => {
    setTipoInmueble(arrendamiento.tipo_inmueble ?? "1");
    setColonia(arrendamiento.inmueble_colonia ?? "");
    setCalle(arrendamiento.inmueble_calle ?? "");
    setNumeroExterior(arrendamiento.inmueble_numero_exterior ?? "");
    setNumeroInterior(arrendamiento.inmueble_numero_interior ?? "");
    setCodigoPostal(arrendamiento.inmueble_codigo_postal ?? "");
    setFolioReal(arrendamiento.folio_real ?? "");
    setFechaInicio(arrendamiento.fecha_inicio);
    setFechaTermino(arrendamiento.fecha_termino);
  }, [arrendamiento]);

  return (
    <div style={st.tarjeta}>
      <p style={st.eyebrow}>Datos para XML ARI</p>
      {faltantes.length > 0 && (
        <div style={{ marginBottom: 10, padding: "8px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 12.5 }}>
          Faltan datos para exportar XML ARI: {faltantes.join(", ")}.
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <div>
          <label style={st.label}>Tipo de inmueble</label>
          <select style={st.input} value={tipoInmueble} onChange={(e) => { setTipoInmueble(e.target.value); }}>
            {catalogos.tiposInmueble.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        </div>
        <div>
          <label style={st.label}>Folio real</label>
          <input style={st.input} maxLength={LONGITUD_FOLIO_REAL} value={folioReal} onChange={(e) => { setFolioReal(claveXml(e.target.value, LONGITUD_FOLIO_REAL)); }} />
        </div>
        <div>
          <label style={st.label}>Colonia</label>
          <input style={st.input} value={colonia} onChange={(e) => { setColonia(claveXml(e.target.value, LONGITUD_DIRECCION_ARI)); }} />
        </div>
        <div>
          <label style={st.label}>Calle</label>
          <input style={st.input} value={calle} onChange={(e) => { setCalle(claveXml(e.target.value, LONGITUD_DIRECCION_ARI)); }} />
        </div>
        <div>
          <label style={st.label}>Número exterior</label>
          <input style={st.input} value={numeroExterior} onChange={(e) => { setNumeroExterior(claveXml(e.target.value, LONGITUD_NUMERO_DIRECCION_ARI)); }} />
        </div>
        <div>
          <label style={st.label}>Número interior</label>
          <input style={st.input} value={numeroInterior} onChange={(e) => { setNumeroInterior(claveXml(e.target.value, LONGITUD_NUMERO_DIRECCION_ARI)); }} />
        </div>
        <div>
          <label style={st.label}>Código postal</label>
          <input style={st.input} inputMode="numeric" value={codigoPostal} onChange={(e) => { setCodigoPostal(e.target.value.replace(/[^0-9]/g, "")); }} />
        </div>
        <div>
          <label style={st.label}>Fecha de inicio del contrato</label>
          <input style={st.input} type="date" value={fechaInicio} onChange={(e) => { setFechaInicio(e.target.value); }} />
        </div>
        <div>
          <label style={st.label}>Fecha de término del contrato</label>
          <input style={st.input} type="date" value={fechaTermino} onChange={(e) => { setFechaTermino(e.target.value); }} />
        </div>
      </div>
      <button
        style={{ ...st.btn, marginTop: 10, color: C.tielDark, borderColor: C.tiel, opacity: guardando || ocupado ? OPACIDAD_DESHABILITADO : 1 }}
        disabled={guardando || ocupado}
        onClick={() => {
          setGuardando(true);
          void onGuardar({
            tipo_inmueble: tipoInmueble,
            inmueble_colonia: textoONull(colonia),
            inmueble_calle: textoONull(calle),
            inmueble_numero_exterior: textoONull(numeroExterior),
            inmueble_numero_interior: textoONull(numeroInterior),
            inmueble_codigo_postal: textoONull(codigoPostal),
            folio_real: textoONull(folioReal),
            fecha_inicio: textoONull(fechaInicio),
            fecha_termino: textoONull(fechaTermino),
          }).finally(() => { setGuardando(false); });
        }}
      >
        {guardando ? "Guardando…" : "Guardar datos ARI"}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cobros de renta del arrendamiento (lista + alta inline)
// ─────────────────────────────────────────────────────────────────────────────

function CobrosCard({
  demo,
  catalogos,
  cobros,
  evals,
  hipotesis,
  onRegistrar,
}: {
  demo: boolean;
  catalogos: CatalogosAri;
  cobros: AportacionRow[] | null;
  evals: Map<string, EvaluacionMotor>;
  hipotesis: Map<string, HipotesisNormativa[]>;
  onRegistrar: (datos: Record<string, unknown>) => Promise<void>;
}) {
  const [abierto, setAbierto] = useState(false);
  const [base, setBase] = useState("");
  const [iva, setIva] = useState("");
  const [fechaCobro, setFechaCobro] = useState("");
  const [instrumento, setInstrumento] = useState("8");
  const [formaPago, setFormaPago] = useState("1");
  const [moneda, setMoneda] = useState("1");
  const [guardando, setGuardando] = useState(false);

  const nBase = Number(base) || 0;
  const nIva = Number(iva) || 0;
  const listo = nBase > 0 && fechaCobro !== "";

  return (
    <div style={st.tarjeta}>
      <p style={st.eyebrow}>Cobros de renta de este arrendamiento ({String(cobros?.length ?? 0)})</p>
      {cobros === null && <p style={{ color: C.muted, fontSize: 13 }}>Cargando cobros…</p>}
      {cobros !== null && cobros.length === 0 && (
        <p style={{ color: C.muted, fontSize: 13 }}>Sin cobros de renta registrados todavía.</p>
      )}
      {cobros?.map((c) => {
        const v = veredictoCobro(evals.get(c.id));
        const instrumentoTxt = etiquetaCatalogo(catalogos.instrumentos, c.instrumento_monetario, c.es_efectivo ? "Efectivo" : "Instrumento");
        const monedaTxt = etiquetaCatalogo(catalogos.monedas, c.moneda, "Moneda");
        const formaPagoTxt = etiquetaCatalogo(catalogos.formasPago, c.forma_pago_inm, "Forma ARI");
        return (
          <div key={c.id} style={{ padding: "8px 0", borderBottom: `1px solid ${C.line}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 13.5 }}>
              <span style={{ color: C.ink }}>
                <b>{dinero(c.monto_bruto)}</b>
                <span style={{ color: C.muted }}> · {instrumentoTxt} · {monedaTxt} · {formaPagoTxt} · {fecha(c.fecha_acto)}</span>
              </span>
            </div>
            {v && (
              <div style={{ marginTop: 4, fontSize: 12, color: { verde: C.green, ambar: C.amber, rojo: C.red, gris: C.muted }[v.tono] }}>
                ● {v.texto}
              </div>
            )}
            <HipotesisInline hipotesis={hipotesis.get(c.id)} />
          </div>
        );
      })}

      {!abierto ? (
        <button style={{ ...st.btn, marginTop: 10, color: C.tielDark, borderColor: C.tiel }} onClick={() => { setAbierto(true); }}>
          ＋ Registrar cobro de renta
        </button>
      ) : (
        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={st.label}>Monto sin IVA</label>
            <input style={st.input} inputMode="numeric" placeholder="0" value={base} onChange={(e) => { setBase(e.target.value.replace(/[^0-9.]/g, "")); }} />
          </div>
          <div>
            <label style={st.label}>IVA (si aplica)</label>
            <input style={st.input} inputMode="numeric" placeholder="0" value={iva} onChange={(e) => { setIva(e.target.value.replace(/[^0-9.]/g, "")); }} />
          </div>
          <div>
            <label style={st.label}>Fecha del cobro efectivo</label>
            <input style={st.input} type="date" value={fechaCobro} onChange={(e) => { setFechaCobro(e.target.value); }} />
          </div>
          <div>
            <label style={st.label}>Instrumento</label>
            <select style={st.input} value={instrumento} onChange={(e) => { setInstrumento(e.target.value); }}>
              {catalogos.instrumentos.map(([v, t]) => (
                <option key={v} value={v}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={st.label}>Forma de pago ARI</label>
            <select style={st.input} value={formaPago} onChange={(e) => { setFormaPago(e.target.value); }}>
              {catalogos.formasPago.map(([v, t]) => (
                <option key={v} value={v}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={st.label}>Moneda</label>
            {/* #38: la captura en moneda ≠ MXN se bloquea hasta tener conversión
                con tipo de cambio DOF (en desarrollo). Comparar un monto en dólares
                contra el umbral en pesos sin convertir arriesga NO detectar la
                obligación de aviso — el peor fallo en cumplimiento. El motor
                también lo bloquea (defensa en profundidad). */}
            <select style={{ ...st.input, background: C.grayBg, cursor: "not-allowed" }} value="1" disabled>
              <option value="1">Peso mexicano (MXN)</option>
            </select>
            <div style={{ marginTop: 6, padding: "7px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 12 }}>
              Captura en moneda extranjera no soportada aún: la conversión con tipo de
              cambio DOF está en desarrollo. Registra el cobro en pesos (MXN).
            </div>
          </div>
          <div style={{ gridColumn: "span 2", display: "flex", gap: 8 }}>
            <button
              style={{ ...st.btnPrim, opacity: guardando || !listo ? OPACIDAD_DESHABILITADO : 1 }}
              disabled={guardando || !listo}
              onClick={() => {
                setGuardando(true);
                void onRegistrar({
                  fecha_acto: fechaCobro,
                  monto_base: nBase,
                  monto_contribuciones: nIva,
                  monto_bruto: nBase + nIva,
                  es_efectivo: instrumento === "1",
                  instrumento_monetario: instrumento,
                  forma_pago_inm: formaPago,
                  moneda,
                  tipo_aportacion: "terceros",
                }).then(() => {
                  setAbierto(false);
                  setBase(""); setIva(""); setFechaCobro(""); setFormaPago("1"); setMoneda("1");
                }).catch(() => {
                  // El contenedor padre ya muestra el error; conserva la captura para reintentar.
                }).finally(() => {
                  setGuardando(false);
                });
              }}
            >
              {guardando ? "Registrando…" : demo ? "Registrar (requiere backend)" : "Registrar cobro — el motor evalúa"}
            </button>
            <button style={st.btn} onClick={() => { setAbierto(false); }}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Alta de arrendamiento
// ─────────────────────────────────────────────────────────────────────────────

function FormArrendamiento({
  demo,
  catalogos,
  personas,
  proyectos,
  onCrear,
  onCancelar,
}: {
  demo: boolean;
  catalogos: CatalogosAri;
  personas: PersonaRow[];
  proyectos: ProyectoMin[];
  onCrear: (datos: Record<string, unknown>) => Promise<void>;
  onCancelar: () => void;
}) {
  const [unidad, setUnidad] = useState("");
  const [personaId, setPersonaId] = useState("");
  const [proyectoId, setProyectoId] = useState("");
  const [rentaMensual, setRentaMensual] = useState("");
  const [fecha, setFecha] = useState("");
  const [tipoInmueble, setTipoInmueble] = useState("1");
  const [colonia, setColonia] = useState("");
  const [calle, setCalle] = useState("");
  const [numeroExterior, setNumeroExterior] = useState("");
  const [numeroInterior, setNumeroInterior] = useState("");
  const [codigoPostal, setCodigoPostal] = useState("");
  const [folioReal, setFolioReal] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaTermino, setFechaTermino] = useState("");
  const [guardando, setGuardando] = useState(false);

  const persona = personas.find((p) => p.id === personaId);
  const faltantes = faltantesDatosAri({ colonia, calle, numeroExterior, codigoPostal, folioReal, fechaInicio, fechaTermino });
  const listo = unidad.trim() !== "" && personaId !== "" && proyectoId !== "" && (Number(rentaMensual) || 0) > 0 && fecha !== "" && faltantes.length === 0;

  const nombreDe = (p: PersonaRow): string => {
    if (p.tipo === "pm") return p.denominacion_razon ?? "(sin razón social)";
    if (p.tipo === "fideicomiso") return p.fideicomiso_identificador ? `Fideicomiso ${p.fideicomiso_identificador}` : "(fideicomiso)";
    return [p.nombre, p.apellido_paterno, p.apellido_materno].filter((x): x is string => !!x).join(" ") || "(sin nombre)";
  };

  return (
    <div style={{ maxWidth: 560, margin: "0 auto" }}>
      <button onClick={onCancelar} style={{ border: "none", background: "transparent", color: C.muted, fontSize: 13, cursor: "pointer", padding: 0, marginBottom: 8 }}>
        ‹ Cancelar
      </button>
      <h2 style={{ margin: "0 0 2px", fontSize: 20 }}>Registrar arrendamiento</h2>
      <p style={{ margin: "0 0 14px", color: C.muted, fontSize: 13 }}>
        El contrato de arrendamiento: inmueble, arrendatario, renta pactada y vigencia. Los
        cobros de renta se registran después, dentro de la misma operación, y cada uno lo
        evalúa el motor con acumulación mensual.
      </p>

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>Operación</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={{ gridColumn: "span 2" }}>
            <label style={st.label}>Unidad</label>
            <input style={st.input} placeholder="Ej. Local 12 · Plaza Norte" value={unidad} onChange={(e) => { setUnidad(e.target.value); }} />
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <label style={st.label}>Arrendatario (expediente ya capturado)</label>
            <select style={st.input} value={personaId} onChange={(e) => { setPersonaId(e.target.value); }}>
              <option value="">Selecciona…</option>
              {personas.map((p) => (
                <option key={p.id} value={p.id}>{nombreDe(p)}</option>
              ))}
            </select>
            {persona && !persona.expediente_completo && (
              <div style={{ marginTop: 6, padding: "7px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 12 }}>
                El expediente del arrendatario está incompleto: puedes registrar el contrato, pero
                cumplimiento deberá completarlo antes de avanzar la operación.
              </div>
            )}
          </div>
          <div style={{ gridColumn: "span 2" }}>
            <label style={st.label}>Proyecto</label>
            <select style={st.input} value={proyectoId} onChange={(e) => { setProyectoId(e.target.value); }}>
              <option value="">Selecciona…</option>
              {proyectos.map((p) => (
                <option key={p.id} value={p.id}>{p.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={st.label}>Renta mensual pactada</label>
            <input style={st.input} inputMode="numeric" placeholder="0" value={rentaMensual} onChange={(e) => { setRentaMensual(e.target.value.replace(/[^0-9.]/g, "")); }} />
          </div>
          <div>
            <label style={st.label}>Fecha de la operación</label>
            <input style={st.input} type="date" value={fecha} onChange={(e) => { setFecha(e.target.value); }} />
          </div>
          <div>
            <label style={st.label}>Fecha de inicio del contrato</label>
            <input style={st.input} type="date" value={fechaInicio} onChange={(e) => { setFechaInicio(e.target.value); }} />
          </div>
          <div>
            <label style={st.label}>Fecha de término del contrato</label>
            <input style={st.input} type="date" value={fechaTermino} onChange={(e) => { setFechaTermino(e.target.value); }} />
          </div>
        </div>
      </div>

      <div style={st.tarjeta}>
        <p style={st.eyebrow}>Datos para XML ARI</p>
        {faltantes.length > 0 && (
          <div style={{ marginBottom: 10, padding: "8px 10px", borderRadius: 8, background: C.amberBg, color: C.amber, fontSize: 12.5 }}>
            Faltan datos para exportar XML ARI: {faltantes.join(", ")}.
          </div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={st.label}>Tipo de inmueble</label>
            <select style={st.input} value={tipoInmueble} onChange={(e) => { setTipoInmueble(e.target.value); }}>
              {catalogos.tiposInmueble.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={st.label}>Folio real</label>
            <input style={st.input} maxLength={LONGITUD_FOLIO_REAL} value={folioReal} onChange={(e) => { setFolioReal(claveXml(e.target.value, LONGITUD_FOLIO_REAL)); }} />
          </div>
          <div>
            <label style={st.label}>Colonia</label>
            <input style={st.input} value={colonia} onChange={(e) => { setColonia(claveXml(e.target.value, LONGITUD_DIRECCION_ARI)); }} />
          </div>
          <div>
            <label style={st.label}>Calle</label>
            <input style={st.input} value={calle} onChange={(e) => { setCalle(claveXml(e.target.value, LONGITUD_DIRECCION_ARI)); }} />
          </div>
          <div>
            <label style={st.label}>Número exterior</label>
            <input style={st.input} value={numeroExterior} onChange={(e) => { setNumeroExterior(claveXml(e.target.value, LONGITUD_NUMERO_DIRECCION_ARI)); }} />
          </div>
          <div>
            <label style={st.label}>Número interior</label>
            <input style={st.input} value={numeroInterior} onChange={(e) => { setNumeroInterior(claveXml(e.target.value, LONGITUD_NUMERO_DIRECCION_ARI)); }} />
          </div>
          <div>
            <label style={st.label}>Código postal</label>
            <input style={st.input} inputMode="numeric" value={codigoPostal} onChange={(e) => { setCodigoPostal(e.target.value.replace(/[^0-9]/g, "")); }} />
          </div>
        </div>
      </div>

      <button
        style={{ ...st.btnPrim, width: "100%", opacity: guardando || !listo ? OPACIDAD_DESHABILITADO : 1 }}
        disabled={guardando || !listo}
        onClick={() => {
          setGuardando(true);
          void onCrear({
            unidad: unidad.trim(),
            cliente_aportante_id: personaId,
            proyecto_id: proyectoId,
            renta_mensual: Number(rentaMensual),
            fecha_operacion: fecha,
            fecha_inicio: fechaInicio,
            fecha_termino: fechaTermino,
            tipo_inmueble: tipoInmueble,
            inmueble_colonia: textoONull(colonia),
            inmueble_calle: textoONull(calle),
            inmueble_numero_exterior: textoONull(numeroExterior),
            inmueble_numero_interior: textoONull(numeroInterior),
            inmueble_codigo_postal: textoONull(codigoPostal),
            folio_real: textoONull(folioReal),
          }).finally(() => { setGuardando(false); });
        }}
      >
        {guardando ? "Guardando…" : demo ? "Registrar (requiere backend)" : "Registrar arrendamiento"}
      </button>
      <p style={{ fontSize: 12, color: C.muted, textAlign: "center", margin: "10px 0 24px" }}>
        Los cobros de renta se capturan en el detalle; cada uno vigila el límite de efectivo con IVA.
      </p>
    </div>
  );
}
